import React, { ComponentProps, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Box, styled } from '@applatform/design-system';
import { fadeIn } from 'theme/animations';
import Spinner from 'components/V1/Spinner/Spinner';

const StyledButton = styled('button', {
  // Reset
  alignItems: 'center',
  appearance: 'none',
  boxSizing: 'border-box',
  display: 'inline-flex',
  justifyContent: 'center',
  lineHeight: '1',
  outline: 'none',
  padding: '0',
  textDecoration: 'none',
  textAlign: 'center',
  userSelect: 'none',
  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
  backgroundColor: 'transparent',
  border: 'none',
  fontFamily: 'inherit',
  color: 'inherit',
  '&:not(:disabled)': {
    cursor: 'pointer',
  },
  '&::before': {
    boxSizing: 'border-box',
  },
  '&::after': {
    boxSizing: 'border-box',
  },

  // Custom
  position: 'relative',
  '&:focus': {
    boxShadow: '$focus',
  },
  '&:focus:not(:focus-visible)': {
    boxShadow: 'none',
  },

  variants: {
    fullWidth: {
      true: {
        width: '100%',
      },
    },
    isLoading: {
      true: {
        color: 'transparent',
        '&:disabled': {
          color: 'transparent',
        },
      },
    },
    size: {
      sm: {
        height: '$10',
        fontSize: '$sm',
      },
      md: {
        height: '$12',
        fontSize: '$md',
      },
      lg: {
        height: '$16',
        fontSize: '$md',
      },
    },
    variant: {
      primary: {
        backgroundColor: '$primary',
        color: '$white',
        px: '$8',
        borderRadius: '$xs',
        fontWeight: '$medium',
        letterSpacing: '$wide',
        '&:disabled': {
          opacity: 0.6,
        },
        '&::after': {
          content: `''`,
          borderRadius: '$xs',
          position: 'absolute',
          zIndex: -1,
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          boxShadow: '0px 4px 16px #bdbdbd',
          transition: `all 250ms ease`,
        },
        '&:hover::after': {
          opacity: 1,
        },
        '&:focus::after': {
          opacity: 1,
        },
        [`& ${Spinner}`]: {
          color: '$white',
        },
      },
      primary2: {
        backgroundColor: '$violet900',
        color: '$white',
        p: '$3 $6',
        borderRadius: '$sm',
        fontWeight: '$medium',
        letterSpacing: '$wide',
        transition: 'all 200ms ease',
        fontSize: '$sm',

        '&:hover:not(:disabled)': {
          backgroundColor: '$violet600',
        },
        '&:disabled': {
          opacity: 0.6,
        },
        '&:hover::after': {
          opacity: 1,
        },
        '&:focus::after': {
          opacity: 1,
        },
        [`& ${Spinner}`]: {
          color: '$white',
        },
      },
      secondary: {
        backgroundColor: '$violet100',
        color: '$secondary',
        px: '$5',
        borderRadius: '$sm',
        fontWeight: '$medium',
        letterSpacing: '$wide',
        transition: `all 250ms ease`,
        '&:hover:not(:disabled)': {
          color: '$white',
          backgroundColor: '$secondary',
        },
        '&:disabled': {
          opacity: 0.5,
        },
        [`& ${Spinner}`]: {
          color: '$secondary',
          height: '16px',
          width: '16px',
        },
      },
      tertiary: {
        px: '$4',
        backgroundColor: '$white',
        border: '1px solid',
        borderColor: '$border1',
        borderRadius: '$sm',
        color: '$accessibleGrey',
        fontWeight: '$medium',
        letterSpacing: '$wide',
        transition: `all 250ms ease`,
        '&:hover:not(:disabled)': {
          backgroundColor: '$violet100',
        },
        '&:disabled': {
          opacity: 0.5,
        },
        [`& ${Spinner}`]: {
          color: '$secondary',
          height: '16px',
          width: '16px',
        },
      },
      ghost: {
        color: '$secondary',
        height: '$5',
        textUnderlineOffset: '$space$1',
        '&:hover:not(:disabled)': {
          textDecoration: 'underline',
        },
      },
    },
  },
});

type ButtonProps = Omit<ComponentProps<typeof StyledButton>, 'as'> & {
  isLoading?: boolean;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, disabled, isLoading = false, ...props }, forwardedRef) => {
    return (
      <StyledButton
        {...props}
        disabled={disabled || isLoading}
        isLoading={isLoading}
        ref={forwardedRef}
      >
        {isLoading ? (
          <Box
            as="span"
            css={{ position: 'absolute', animation: `${fadeIn} 400ms` }}
          >
            <Spinner />
          </Box>
        ) : null}
        {children}
      </StyledButton>
    );
  }
);

export default Button;

/*
 * A link styled as a Button
 */
export const LinkButton = styled(Link, StyledButton);
